import { useEffect, useId, useRef } from "react";
import { Link, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import {
	ArrowLeft,
	ArrowRight,
	Gamepad2,
} from "lucide-react";
import type { CmsItem } from "@hive/sdk";
import { observer } from "mobx-react-lite";
import type { CmsItemsSection } from "../../stores/discoverStore";
import { useStore } from "../../stores/rootStore";
import { cmsWebHref, explorePresentation, EXPLORE_PREVIEW_LIMIT, type ExplorePresentation } from "../../lib/exploreContent";
import { playableKind, playablePath } from "../../lib/playableKind";
import { topicPath } from "../../lib/topics";
import { DistributionCard, DistributionHeroCreative } from "./DistributionCard";
import { ExploreCarousel } from "./ExploreCarousel";
import { ExploreAutoPreview } from "./ExploreAutoPreview";
import { CatalogueGrid } from "./CatalogueGrid";
import type { CatalogueSize } from "../../lib/catalogueLayout";
import { catalogueOrder, sortCatalogue } from "../../lib/catalogueOrder";
import { CatalogueSort } from "./CatalogueSort";
import { CoverImage } from "../ui/CoverImage";
import { Skeleton } from "../ui/Skeleton";
import { EmptyState } from "../ui/EmptyState";

export const ExploreShelves = observer(function ExploreShelves({
	sectionId,
}: {
	sectionId: string | null;
}) {
	const { explore, auth } = useStore();
	const { t } = useTranslation();
	const shelves = useRef<HTMLDivElement>(null);
	const currentSections = explore.data?.sections.filter((section) => !section.playHistory || explore.isViewer(auth.user?.id ?? null));
	const selected = currentSections?.find((section) => section.id === sectionId);
	const sections = currentSections?.filter((section) => section.items.length > 0) ?? [];

	useEffect(() => {
		if (sectionId && explore.data && !explore.loading) void explore.loadCollection(sectionId);
	}, [sectionId, explore, explore.data?.manifest.revision, explore.loading]);

	if (!explore.data && !explore.error) return <ExploreSkeleton />;
	if (selected)
		return (
			<div className="space-y-6">
				<Link
					to="/"
					className="inline-flex items-center gap-2 text-sm font-medium text-muted hover:text-text"
				>
					<ArrowLeft size={16} />
					{t("discover.backToExplore")}
				</Link>
				{explore.collectionsFailed.has(selected.id) ? (
					<RetryMessage onRetry={() => void explore.loadCollection(selected.id)} />
				) : null}
				<Shelf key={selected.id} section={selected} full />
				{explore.collectionsLoading.has(selected.id) ? (
					<p role="status" className="text-sm text-muted">
						{t("discover.loadingContent")}
					</p>
				) : null}
			</div>
		);
	if (!sections.length && !explore.error)
		return (
			<EmptyState
				icon={Gamepad2}
				title={t("discover.emptyTitle")}
				description={t("discover.emptyDesc")}
			/>
		);
	return (
		<div ref={shelves} className="explore-shelves">
			<ExploreAutoPreview container={shelves} paused={false} />
			{sections.map((section) => (
				<Shelf key={section.id} section={section} />
			))}
		</div>
	);
});

export function RetryMessage({ onRetry }: { onRetry: () => void }) {
	const { t } = useTranslation();
	return (
		<div
			role="alert"
			className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-line bg-surface px-4 py-3 text-sm text-muted"
		>
			<span>{t("discover.loadFailed")}</span>
			<button
				type="button"
				onClick={onRetry}
				className="font-semibold text-text underline underline-offset-4"
			>
				{t("common.retry")}
			</button>
		</div>
	);
}

export function ExploreSkeleton() {
	return (
		<div role="status" className="space-y-8">
			<span className="sr-only">Loading</span>
			{[0, 1].map((row) => (
				<div key={row} className="space-y-4">
					<Skeleton className="h-6 w-40 rounded" />
					<div className="explore-rail">
						{Array.from({ length: 10 }, (_, i) => (
							<Skeleton key={i} className="aspect-[512/772] rounded-xl" />
						))}
					</div>
				</div>
			))}
		</div>
	);
}

const Shelf = observer(function Shelf({
	section,
	full = false,
}: {
	section: CmsItemsSection;
	full?: boolean;
}) {
	const { explore } = useStore();
	const { t } = useTranslation();
	const id = useId();
	const [params, setParams] = useSearchParams();
	const sortable = full && section.layout === "GRID" && !section.playHistory;
	const order = sortable ? catalogueOrder(params.get("sort")) : "featured";
	const presentation = explore.data ? explorePresentation(explore.data.manifest, section.id) : "grid";
	const rail = !full;
	const playableOnly = section.layout === "GRID";
	const definition = explore.data?.manifest.sections.find((entry) => entry.id === section.id);
	const more =
		definition?.layout !== "FEED" && (definition?.items.length ?? 0) > EXPLORE_PREVIEW_LIMIT;
	const href =
		section.headerTarget?.type === "topic"
			? topicPath(section.headerTarget.tag)
			: "/?section=" + encodeURIComponent(section.id);
	const showAll =
		!full && !section.playHistory &&
		(more || !!section.headerTarget || section.items.length > 1);
	const visibleItems = section.items
		.filter((item) => item.type === "playable" || (!playableOnly && !!item.imageUrl));
	const games = visibleItems.filter((item): item is Extract<CmsItem, { type: "playable" }> => item.type === "playable");
	const sortedGames = sortCatalogue(games, order, (item) => item.playable);

	if (!visibleItems.length) return null;
	const content = full && order !== "featured" && !explore.isCollectionLoaded(section.id) && (definition?.layout !== "FEED" && (definition?.items.length ?? 0) > section.items.length)
				? <ExploreSkeleton /> : full && !section.playHistory && presentation !== "hero" && visibleItems.every((item) => item.type === "playable") ? <CatalogueGrid
				key={section.id + ":" + order}
				items={sortedGames.map((item) => ({ ...item.playable, coverUrl: item.imageUrl ?? item.playable.coverUrl }))}
				seed={section.id} pattern={explore.data?.sections.findIndex((entry) => entry.id === section.id)}
				properties={(item, index) => ({ content_id: item.id, content_kind: playableKind(item), source: "app_explore", position: index + 1 })}
				renderCard={(playable, index, size) => <ExplorePlayableCard key={playable.id + ":" + index}
					item={{ ...sortedGames[index]!, playable }}
					presentation={presentation} section={section} index={index} coverOnly={false} size={size} />}
			/> : <div
				className={full ? "explore-grid" + (presentation === "hero" ? " explore-grid--hero" : "") : "explore-rail explore-rail--" + presentation}
				data-count={visibleItems.length}
			>
				{visibleItems.map((item, index) =>
					item.type === "playable" ? (
						<ExplorePlayableCard
							key={section.id + ":" + index}
							item={item}
							presentation={presentation}
							section={section}
							index={index}
							coverOnly={!full}
						/>
					) : (
						<CreativeCard key={section.id + ":" + index} item={item} presentation={presentation} coverOnly={!full} />
					),
				)}
			</div>;
	return (
		<section aria-labelledby={section.title ? id : undefined} className="min-w-0" data-explore-layout={presentation}>
			{section.title || section.subtitle || rail ? (
				<div className="explore-shelf-heading">
					<div className="min-w-0">
						{section.title ? (
							<h2 id={id} className="text-lg font-bold tracking-tight text-text">
								{section.title}
							</h2>
						) : null}
						{section.subtitle ? (
							<p className="mt-1 text-sm text-muted">{section.subtitle}</p>
						) : null}
					</div>
					<div className="flex shrink-0 items-center gap-2">
						{sortable ? <CatalogueSort value={order}
							disabled={explore.collectionsLoading.has(section.id)}
							onChange={(value) => { const next = new URLSearchParams(params); value === "featured" ? next.delete("sort") : next.set("sort", value); setParams(next); }} /> : null}
						{showAll ? (
							<Link to={href} className="explore-see-all">
								{t("discover.seeAll")}
								<ArrowRight size={15} />
							</Link>
						) : null}
					</div>
				</div>
			) : null}
			{full ? content : <ExploreCarousel id={id + "-items"} title={section.title ?? t("discover.explore")}
				more={more && !explore.isCollectionLoaded(section.id)} loading={explore.collectionsLoading.has(section.id)}
				failed={explore.collectionsFailed.has(section.id)} loadMore={() => explore.loadCollection(section.id)}>
				{content}
			</ExploreCarousel>}
			{!full && explore.collectionsFailed.has(section.id) ? <RetryMessage onRetry={() => void explore.loadCollection(section.id)} /> : null}
		</section>
	);
});

function ExplorePlayableCard({
	item,
	presentation,
	section,
	index,
	coverOnly,
	size = "small",
}: {
	item: Extract<CmsItem, { type: "playable" }>;
	presentation: ExplorePresentation;
	section: CmsItemsSection;
	index: number;
	coverOnly: boolean;
	size?: CatalogueSize;
}) {
	const properties = {
		content_id: item.playable.id,
		content_kind: playableKind(item.playable),
		source: "app_explore",
		feed_tab: "explore",
		category: section.title ?? section.id,
		position: index + 1,
	};
	const href = cmsWebHref(item.linkUrl) ?? playablePath(item.playable);
	return <DistributionCard
		item={item.playable}
		hero={presentation === "hero"}
		coverOnly={coverOnly}
		title={item.title}
		imageUrl={item.imageUrl}
		href={href}
		properties={properties}
		coverSizes={presentation === "hero" ? "600px" : size === "large" ? "300px" : !coverOnly ? "240px" : presentation === "grid" ? "208px" : "164px"}
	/>;
}

function CreativeCard({ item, presentation, coverOnly }: {
	item: Extract<CmsItem, { type: "creative" }>;
	presentation: ExplorePresentation;
	coverOnly: boolean;
}) {
	const href = cmsWebHref(item.linkUrl);
	if (presentation === "hero") return <DistributionHeroCreative title={item.title} imageUrl={item.imageUrl} href={href ?? undefined} />;
	const body = <>
		<div className="distribution-cover">
			{item.imageUrl ? <CoverImage src={item.imageUrl} alt="" loading="lazy" className="size-full" fallback={<Gamepad2 className="m-auto text-muted" />} /> : null}
		</div>
		{!coverOnly ? <div className="distribution-caption"><h3>{item.title}</h3></div> : <span className="sr-only">{item.title}</span>}
	</>;
	return <article className="distribution-card distribution-creative">
		{href?.startsWith("/") ? <Link to={href} className="distribution-card-link">{body}</Link>
			: href ? <a href={href} target="_blank" rel="noopener noreferrer" className="distribution-card-link">{body}</a>
			: <div>{body}</div>}
	</article>;
}
